import React, { useState } from "react";
import * as XLSX from "xlsx";
import "./CompetenciesList.css";
import { useGetMyCompanyCompetencySetQuery } from "../../services/api";
import { FlowchartView } from "./components/flowchart/FlowchartView";
import { DetailedFlowchartView } from "./components/flowchart/DetailedFlowchartView";
import { ExternalLink, RoleBadge } from "./components/shared";

// ─────────────────────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────────────────────
const ChevronDown = ({ className }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
  </svg>
);

const AccordionIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);

const TableIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="3" x2="9" y2="21" />
  </svg>
);

const FlowLRIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="9" width="4" height="6" rx="1" />
    <rect x="10" y="4" width="4" height="5" rx="1" />
    <rect x="10" y="15" width="4" height="5" rx="1" />
    <rect x="18" y="9" width="4" height="6" rx="1" />
    <line x1="6" y1="12" x2="10" y2="6.5" />
    <line x1="6" y1="12" x2="10" y2="17.5" />
    <line x1="14" y1="6.5" x2="18" y2="12" />
    <line x1="14" y1="17.5" x2="18" y2="12" />
  </svg>
);

const FlowTBIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="4" rx="1" />
    <rect x="4" y="10" width="5" height="4" rx="1" />
    <rect x="15" y="10" width="5" height="4" rx="1" />
    <rect x="9" y="18" width="6" height="4" rx="1" />
    <line x1="12" y1="6" x2="6.5" y2="10" />
    <line x1="12" y1="6" x2="17.5" y2="10" />
    <line x1="6.5" y1="14" x2="12" y2="18" />
    <line x1="17.5" y1="14" x2="12" y2="18" />
  </svg>
);

const FlowDetailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="10" width="4" height="4" rx="1" />
    <rect x="9" y="5" width="4" height="4" rx="1" />
    <rect x="9" y="15" width="4" height="4" rx="1" />
    <rect x="16" y="3" width="3" height="3" rx="0.5" />
    <rect x="16" y="8" width="3" height="3" rx="0.5" />
    <rect x="16" y="13" width="3" height="3" rx="0.5" />
    <rect x="16" y="18" width="3" height="3" rx="0.5" />
    <line x1="6" y1="12" x2="9" y2="7" />
    <line x1="6" y1="12" x2="9" y2="17" />
    <line x1="13" y1="7" x2="16" y2="4.5" />
    <line x1="13" y1="7" x2="16" y2="9.5" />
    <line x1="13" y1="17" x2="16" y2="14.5" />
    <line x1="13" y1="17" x2="16" y2="19.5" />
  </svg>
);

// ─────────────────────────────────────────────────────────────
// Shared Components
// ─────────────────────────────────────────────────────────────
function LearningExperience({ learning, variant = "default" }) {
  if (!learning) return null;

  // Normalize to array
  const items = Array.isArray(learning) ? learning : [learning];
  if (items.length === 0) return null;

  if (variant === "accordion") {
    return (
      <div className="accordion-learning-list">
        {items.map((item, idx) => (
          <div key={idx} className="accordion-learning">
            <span className="accordion-learning-type">{item.type}</span>
            {item.url ? (
              <a href={item.url} target="_blank" rel="noopener noreferrer" className="accordion-learning-link">
                {item.title}
                <ExternalLink />
              </a>
            ) : (
              <span className="accordion-learning-title">{item.title}</span>
            )}
            {item.description && <p className="accordion-learning-desc">{item.description}</p>}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="learning-experience-list">
      {items.map((item, idx) => (
        <div key={idx} className="learning-experience">
          <div className="learning-header">
            <span className="learning-type-badge">{item.type}</span>
          </div>
          {item.url ? (
            <a href={item.url} target="_blank" rel="noopener noreferrer" className="learning-title-link">
              {item.title}
              <ExternalLink />
            </a>
          ) : (
            <span className="learning-title-text">{item.title}</span>
          )}
          {item.description && <p className="learning-description">{item.description}</p>}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// View Switcher Component
// ─────────────────────────────────────────────────────────────
function ViewSwitcher({ activeView, onViewChange }) {
  const views = [
    // { id: "card", label: "Card", icon: <CardIcon /> },
    // { id: "accordion", label: "Accordion", icon: <AccordionIcon /> },
    { id: "table", label: "Table", icon: <TableIcon /> },
    { id: "flowchart-lr", label: "Flow (H)", icon: <FlowLRIcon /> },
    // { id: "flowchart-tb", label: "Flow (V)", icon: <FlowTBIcon /> },
    // { id: "flowchart-detailed", label: "Detailed", icon: <FlowDetailIcon /> },
  ];

  return (
    <div className="view-switcher">
      {views.map((view) => (
        <button
          key={view.id}
          className={`view-switcher-btn ${activeView === view.id ? "active" : ""}`}
          onClick={() => onViewChange(view.id)}
        >
          {view.icon}
          <span>{view.label}</span>
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Card View Components
// ─────────────────────────────────────────────────────────────
function ActionCard({ action }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="action-card">
      <button className="action-header" onClick={() => setExpanded(!expanded)}>
        <span className="action-name">{action.actionName}</span>
        <ChevronDown className={`action-chevron ${expanded ? "rotated" : ""}`} />
      </button>

      {expanded && (
        <div className="action-content">
          {action.entries?.map((entry, idx) => (
            <div key={idx} className="entry-row">
              <div className="entry-columns">
                {entry.skill && (
                  <div className="entry-column">
                    <span className="entry-label">Skill</span>
                    <p className="entry-text">{entry.skill}</p>
                  </div>
                )}
                {entry.attitude && (
                  <div className="entry-column">
                    <span className="entry-label">Attitude</span>
                    <p className="entry-text">{entry.attitude}</p>
                  </div>
                )}
              </div>

              {entry.roles?.length > 0 && (
                <div className="entry-roles">
                  <span className="entry-label">Roles</span>
                  <div className="roles-list">
                    {entry.roles.map((role, rIdx) => (
                      <RoleBadge key={rIdx} role={role} />
                    ))}
                  </div>
                </div>
              )}

              <LearningExperience learning={entry.learning} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CompetencySection({ competency }) {
  return (
    <div className="competency-section">
      <h4 className="competency-name">{competency.competencyName}</h4>
      <div className="actions-list">
        {competency.actions?.map((action, idx) => (
          <ActionCard key={idx} action={action} />
        ))}
      </div>
    </div>
  );
}

function ValueCard({ value }) {
  const [expanded, setExpanded] = useState(false);
  const totalActions = value.competencies?.reduce((sum, c) => sum + (c.actions?.length || 0), 0) || 0;

  return (
    <div className={`value-card ${expanded ? "expanded" : ""}`}>
      <button className="value-header" onClick={() => setExpanded(!expanded)}>
        <div className="value-info">
          <h3 className="value-name">{value.valueName}</h3>
          <span className="value-stats">
            {value.competencies?.length || 0} competencies, {totalActions} actions
          </span>
        </div>
        <ChevronDown className={`value-chevron ${expanded ? "rotated" : ""}`} />
      </button>

      {expanded && (
        <div className="value-content">
          {value.competencies?.map((competency, idx) => (
            <CompetencySection key={idx} competency={competency} />
          ))}
        </div>
      )}
    </div>
  );
}

function CardView({ values }) {
  return (
    <div className="values-container">
      {values.map((value, idx) => (
        <ValueCard key={idx} value={value} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Accordion View Components
// ─────────────────────────────────────────────────────────────
function ActionAccordion({ action }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="accordion-item accordion-item--action">
      <button
        className={`accordion-trigger accordion-trigger--action ${expanded ? "expanded" : ""}`}
        onClick={() => setExpanded(!expanded)}
      >
        <span className="accordion-trigger-text">{action.actionName}</span>
        <ChevronDown className={`accordion-chevron ${expanded ? "rotated" : ""}`} />
      </button>
      {expanded && (
        <div className="accordion-content accordion-content--action">
          {action.entries?.map((entry, idx) => (
            <div key={idx} className="accordion-entry">
              <div className="accordion-entry-grid">
                {entry.skill && (
                  <div className="accordion-entry-col">
                    <span className="accordion-label">Skill</span>
                    <p className="accordion-text">{entry.skill}</p>
                  </div>
                )}
                {entry.attitude && (
                  <div className="accordion-entry-col">
                    <span className="accordion-label">Attitude</span>
                    <p className="accordion-text">{entry.attitude}</p>
                  </div>
                )}
              </div>
              {entry.roles?.length > 0 && (
                <div className="accordion-roles">
                  <span className="accordion-label">Roles</span>
                  <div className="accordion-roles-list">
                    {entry.roles.map((role, rIdx) => (
                      <RoleBadge key={rIdx} role={role} variant="accordion" />
                    ))}
                  </div>
                </div>
              )}
              <LearningExperience learning={entry.learning} variant="accordion" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CompetencyAccordion({ competency }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="accordion-item accordion-item--competency">
      <button
        className={`accordion-trigger accordion-trigger--competency ${expanded ? "expanded" : ""}`}
        onClick={() => setExpanded(!expanded)}
      >
        <span className="accordion-trigger-text">{competency.competencyName}</span>
        <span className="accordion-count">{competency.actions?.length || 0} actions</span>
        <ChevronDown className={`accordion-chevron ${expanded ? "rotated" : ""}`} />
      </button>
      {expanded && (
        <div className="accordion-content accordion-content--competency">
          {competency.actions?.map((action, idx) => (
            <ActionAccordion key={idx} action={action} />
          ))}
        </div>
      )}
    </div>
  );
}

function ValueAccordion({ value }) {
  const [expanded, setExpanded] = useState(false);
  const totalActions = value.competencies?.reduce((sum, c) => sum + (c.actions?.length || 0), 0) || 0;

  return (
    <div className="accordion-item accordion-item--value">
      <button
        className={`accordion-trigger accordion-trigger--value ${expanded ? "expanded" : ""}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="accordion-trigger-info">
          <span className="accordion-trigger-title">{value.valueName}</span>
          <span className="accordion-trigger-stats">
            {value.competencies?.length || 0} competencies, {totalActions} actions
          </span>
        </div>
        <ChevronDown className={`accordion-chevron ${expanded ? "rotated" : ""}`} />
      </button>
      {expanded && (
        <div className="accordion-content accordion-content--value">
          {value.competencies?.map((competency, idx) => (
            <CompetencyAccordion key={idx} competency={competency} />
          ))}
        </div>
      )}
    </div>
  );
}

function AccordionView({ values }) {
  return (
    <div className="accordion-container">
      {values.map((value, idx) => (
        <ValueAccordion key={idx} value={value} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Table View Components
// ─────────────────────────────────────────────────────────────
function flattenData(values) {
  const rows = [];

  values?.forEach((value) => {
    value.competencies?.forEach((competency) => {
      competency.actions?.forEach((action) => {
        action.entries?.forEach((entry, entryIdx) => {
          rows.push({
            valueName: value.valueName,
            competencyName: competency.competencyName,
            actionName: action.actionName,
            skill: entry.skill,
            attitude: entry.attitude,
            roles: entry.roles || [],
            learning: entry.learning,
            isFirstInValue: rows.filter(r => r.valueName === value.valueName).length === 0,
            isFirstInCompetency: rows.filter(r => r.valueName === value.valueName && r.competencyName === competency.competencyName).length === 0,
            isFirstInAction: entryIdx === 0,
          });
        });
      });
    });
  });

  return rows;
}

function downloadExcel(values) {
  const rows = flattenData(values).map((row) => ({
    Value: row.valueName,
    Competency: row.competencyName,
    Action: row.actionName,
    Skill: row.skill || "",
    Attitude: row.attitude || "",
    Roles: row.roles.join(", "),
    Learning: row.learning
      ? (Array.isArray(row.learning) ? row.learning : [row.learning])
          .map((item) => item.title)
          .join(", ")
      : "",
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Competencies");
  XLSX.writeFile(wb, "competency-list.xlsx");
}

function TableView({ values }) {
  const rows = flattenData(values);
  const totalCompetencies = values.reduce((sum, v) => sum + (v.competencies?.length || 0), 0);

  return (
    <>
      <div className="table-wrapper">
        <table className="competencies-table">
          <thead>
            <tr>
              <th className="th-value">Value</th>
              <th className="th-competency">Competency</th>
              <th className="th-action">Action</th>
              <th className="th-skill">Skill</th>
              <th className="th-attitude">Attitude</th>
              <th className="th-roles">Roles</th>
              <th className="th-learning">Learning</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={idx}
                className={`
                  ${row.isFirstInValue ? 'row-value-start' : ''}
                  ${row.isFirstInCompetency ? 'row-competency-start' : ''}
                `}
              >
                <td className="td-value">
                  {row.isFirstInValue && <span className="cell-value">{row.valueName}</span>}
                </td>
                <td className="td-competency">
                  {row.isFirstInCompetency && <span className="cell-competency">{row.competencyName}</span>}
                </td>
                <td className="td-action">
                  {row.isFirstInAction && <span className="cell-action">{row.actionName}</span>}
                </td>
                <td className="td-skill">
                  {row.skill && <span className="cell-text">{row.skill}</span>}
                </td>
                <td className="td-attitude">
                  {row.attitude && <span className="cell-text">{row.attitude}</span>}
                </td>
                <td className="td-roles">
                  <div className="cell-roles">
                    {row.roles.map((role, rIdx) => (
                      <RoleBadge key={rIdx} role={role} variant="table" />
                    ))}
                  </div>
                </td>
                <td className="td-learning">
                  {row.learning && (Array.isArray(row.learning) ? row.learning : [row.learning]).map((item, lIdx) => (
                    <div key={lIdx} className="cell-learning-item">
                      <span className="learning-type-mini">{item.type}</span>
                      {item.url ? (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cell-learning-link"
                          title={item.description || item.title}
                        >
                          <span className="learning-title">{item.title}</span>
                          <ExternalLink />
                        </a>
                      ) : (
                        <span className="learning-title">{item.title}</span>
                      )}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-summary">
        <span className="summary-item">
          <strong>{values.length}</strong> Values
        </span>
        <span className="summary-divider">|</span>
        <span className="summary-item">
          <strong>{totalCompetencies}</strong> Competencies
        </span>
        <span className="summary-divider">|</span>
        <span className="summary-item">
          <strong>{rows.length}</strong> Entries
        </span>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────
export default function CompetenciesList() {
  const [viewMode, setViewMode] = useState("table"); // Default to table view
  const { data: competencySet, isLoading, error } = useGetMyCompanyCompetencySetQuery();
  const values = competencySet?.values || [];

  if (isLoading) {
    return (
      <div className="competencies-loading">
        <p>Loading competencies...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="competencies-error">
        <p>Failed to load competencies. Please try again later.</p>
      </div>
    );
  }

  if (!values.length) {
    return (
      <div className="competencies-empty">
        <h1 className="page-title">Competency List</h1>
        <p className="page-subtitle">No competencies have been assigned to your company yet.</p>
      </div>
    );
  }

  return (
    <>
      <h1 className="page-title">Competency List</h1>
      <p className="page-subtitle">
        View all competencies organized by organizational values. Click to expand and explore actions, skills, attitudes, and learning resources.
      </p>

      <div className="competencies-toolbar">
        <ViewSwitcher activeView={viewMode} onViewChange={setViewMode} />
        <button className="download-excel-btn" onClick={() => downloadExcel(values)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download
        </button>
      </div>

      {/* {viewMode === "card" && <CardView values={values} />} */}
      {/* {viewMode === "accordion" && <AccordionView values={values} />} */}
      {viewMode === "table" && <TableView values={values} />}
      {viewMode === "flowchart-lr" && <FlowchartView values={values} direction="LR" />}
      {/* {viewMode === "flowchart-tb" && <FlowchartView values={values} direction="TB" />} */}
      {/* {viewMode === "flowchart-detailed" && <DetailedFlowchartView values={values} direction="LR" />} */}
    </>
  );
}
